import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { FOCUS_RING } from '@/Components/Marketing/focusRing';
import SmartLink, { hrefKind } from '@/Components/Marketing/SmartLink';
import { useTone } from '@/Components/Marketing/Tone';
import { cn } from '@/lib/utils';

const TONES = {
    light: 'text-mk-orange',
    dark: 'text-mk-night-orange focus-visible:ring-offset-mk-night',
};

const ARROW = 'size-4 transition-transform duration-500 ease-mk motion-reduce:transition-none';

/**
 * The quiet "go further" link that closes a section ("See how the platform
 * works", "All guides and articles"): orange text, the brand orange on light
 * (2026-10-13), night-orange on dark. Its arrow follows Button's rule (audits 26 and 47):
 * ArrowUpRight when the link leaves the site, ArrowRight when it takes the
 * section on to another page, none for a link within the page or, with
 * `arrow={false}`, one beside the section's own call to action. `back` turns
 * it into the way back (an article's "Guides and articles"): the arrow
 * leads, pointing back, and nudges that way. (The same arrow turned round:
 * a second icon here would put a chunk of its own into every page's first
 * load, R52; ArrowUpRight is already in it, with Button.) The nudge is for a
 * fine pointer that hovers, and never under reduced motion: a tap leaves no
 * arrow stuck out of place.
 */
export default function ArrowLink({ href, external, tone, back = false, arrow = true, className, children, ...props }) {
    const resolved = useTone(tone);
    const goes = hrefKind(href, external);
    const Trailing = back || !arrow ? null : { external: ArrowUpRight, internal: ArrowRight }[goes] ?? null;

    return (
        <SmartLink
            href={href}
            external={external}
            className={cn(
                'group inline-flex w-fit items-center gap-1.5 rounded-md text-sm font-medium',
                FOCUS_RING,
                TONES[resolved],
                className,
            )}
            {...props}
        >
            {back && (
                <ArrowRight
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className={cn(ARROW, 'rotate-180 motion-safe:fine:group-hover:-translate-x-0.5')}
                />
            )}
            {children}
            {Trailing && (
                <Trailing
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className={cn(
                        ARROW,
                        'motion-safe:fine:group-hover:translate-x-0.5',
                        Trailing === ArrowUpRight && 'motion-safe:fine:group-hover:-translate-y-0.5',
                    )}
                />
            )}
        </SmartLink>
    );
}
